import React, { useState } from "react";
import { useAppSelector } from "@hypexone/uicore";
import ButtonSpinner from "../../shared/ButtonSpinner";

/** Sky's form field: grey rule, inset top shadow, 12px Tahoma. */
const INPUT_CLASS =
  "h-[30px] w-full rounded-[4px] border border-[#aaa] bg-white px-2 text-[12px] text-black shadow-[inset_0_2px_0_rgba(0,0,0,0.1)] outline-none focus:border-[#666]";

interface ChangePasswordProps {
  oldPassword: string;
  newPassword: string;
  confirmPassword: string;
  updateNewPassword: Function;
  setOldPassword: Function;
  setNewPassword: Function;
  setConfirmPassword: Function;
  resetPassword?: Function;
  disable?: boolean;
}
const ChangePasswordTab: React.FC<ChangePasswordProps> = ({
  oldPassword,
  newPassword,
  confirmPassword,
  updateNewPassword,
  setOldPassword,
  setNewPassword,
  setConfirmPassword,
  resetPassword,
}) => {
  const { langData } = useAppSelector((state) => state.cms);
  const [submitting, setSubmitting] = useState(false);
  // Sky's order: the new password twice, then the current one to authorise it.
  const fieldKeys = [
    {
      label: "New Password",
      langKey: "new_password",
      value: newPassword,
    },
    {
      label: "Confirm Password",
      langKey: "confirm_password",
      value: confirmPassword,
    },
    {
      label: "Old Password",
      langKey: "old_password",
      value: oldPassword,
    },
  ];

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement>,
    detail: { label: any; value: string }
  ) => {
    detail.label === "Old Password"
      ? setOldPassword(e.target.value)
      : detail.label === "New Password"
      ? setNewPassword(e.target.value)
      : setConfirmPassword(e.target.value);
  };

  return (
    <form
      className="w-full max-w-[460px] p-3 font-[Tahoma,Helvetica,sans-serif] text-[12px] text-[#1e1e1e]"
      onSubmit={async (e) => {
        e.preventDefault();
        if (submitting) return;
        setSubmitting(true);
        try {
          await updateNewPassword();
        } finally {
          setSubmitting(false);
        }
      }}
    >
      <dl className="grid grid-cols-[140px_1fr] items-center gap-x-3 gap-y-2 max-sm:grid-cols-1 max-sm:gap-y-1">
        {fieldKeys.map((fieldKey) => {
          const id = `change-password-${fieldKey.langKey}`;
          return (
            <React.Fragment key={fieldKey.langKey}>
              <dt>
                <label htmlFor={id}>{langData?.[fieldKey.langKey] ?? fieldKey.label}</label>
              </dt>
              <dd className="m-0">
                <input
                  id={id}
                  type="password"
                  className={INPUT_CLASS}
                  placeholder={
                    langData?.[`enter_${fieldKey.langKey}`] ||
                    `Enter ${langData?.[fieldKey.langKey] ?? fieldKey.label}`
                  }
                  value={fieldKey.value || ""}
                  onChange={(e) => handleChange(e, fieldKey)}
                />
              </dd>
            </React.Fragment>
          );
        })}
      </dl>
      <div className="mt-3 grid grid-cols-[140px_1fr] gap-x-3 max-sm:grid-cols-1">
        <button
          type="submit"
          disabled={submitting}
          aria-busy={submitting}
          className="col-start-2 flex h-[30px] w-[140px] items-center justify-center gap-2 rounded-[4px] border border-[#222] bg-sky-send-gradient text-[12px] font-bold text-skySendText hover:brightness-110 disabled:cursor-wait disabled:opacity-70 max-sm:col-start-1 max-sm:w-full"
        >
          {submitting && <ButtonSpinner />}
          {langData?.["update_password"] ?? "Update Password"}
        </button>
      </div>
    </form>
  );
};

export default ChangePasswordTab;
